import { formatContentLabel } from '../services/activityLog';
import { DEFAULT_WATCH_SECTIONS } from './watchSections';

const SECTION_TITLES = {
  hero: 'Hero banner',
  marquee: 'Scrolling banner',
  about: 'About section',
  verse: 'Bible verses',
  pastor: 'Meet our pastor',
  sermons: 'Sermons',
  services: 'Service times',
  convention: 'Annual convention',
  calendar: 'Church calendar',
  join: 'Join us',
  story: 'Our story',
  photo: 'Pastor photo',
  mission: 'Mission statement',
  intro: 'Page introduction',
  cards: 'Ministry cards',
  gallery: 'Photo gallery',
  beliefs: 'Core beliefs',
  values: 'Our values',
  sections: 'Video sections',
};

const PAGE_SECTION_ORDER = {
  '/': ['hero', 'marquee', 'about', 'verse', 'pastor', 'sermons', 'services', 'convention', 'calendar', 'join'],
  '/story': ['hero', 'story', 'photo', 'mission'],
  '/ministries': ['hero', 'intro', 'cards'],
  '/watch': ['hero', 'sections'],
  '/gallery': ['hero', 'gallery'],
  '/beliefs': ['hero', 'beliefs', 'values'],
  '/visit-us': ['hero', 'calendar'],
};

const PANEL_LABELS = {
  'home.hero.slides': 'Slideshow images',
  'home.hero.welcome': 'Welcome line',
  'home.hero.name_main': 'Church name — line 1',
  'home.hero.name_secondary': 'Church name — line 2',
  'home.hero.description': 'Tagline / description',
  'home.about.title': 'Section title',
  'home.about.p1': 'First paragraph',
  'home.about.p2': 'Second paragraph',
  'home.about.image': 'About photo',
  'home.verses.items': 'Bible verses',
  'home.pastor.background': 'Section background',
  'home.pastor.title': 'Section title',
  'home.pastor.p1': 'First paragraph',
  'home.pastor.p2': 'Second paragraph',
  'home.pastor.image': 'Pastor photo',
  'home.pastor.name': 'Pastor name',
  'home.sermons.title': 'Section title',
  'home.sermons.items': 'Sermon videos',
  'home.sermons.moreLink': 'View more link',
  'home.services.title': 'Section title',
  'home.services.items': 'Weekly schedule',
  'home.convention.title': 'Section title',
  'home.convention.flyer': 'Convention flyer',
  'home.convention.theme_label': 'Theme label',
  'home.convention.theme': 'Convention theme',
  'home.convention.speaker': 'Guest speaker',
  'home.convention.location': 'Convention location',
  'home.join.title': 'Section title',
  'home.join.description': 'Description',
  'calendar.customEvents': 'Calendar events',
  'story.hero.title': 'Page title',
  'story.heading': 'Section heading',
  'story.p1': 'Paragraph 1',
  'story.p2': 'Paragraph 2',
  'story.p3': 'Paragraph 3',
  'story.quote1': 'Quote 1',
  'story.p4': 'Paragraph 4',
  'story.quote2': 'Quote 2',
  'story.p5': 'Paragraph 5',
  'story.pastor_image': 'Pastor photo',
  'story.image_title': 'Photo caption',
  'story.mission.title': 'Mission title',
  'story.mission.description': 'Mission description',
  'ministries.hero.title': 'Page title',
  'ministries.description': 'Main description',
  'ministries.description_sub': 'Secondary description',
  'gallery.hero.title': 'Page title',
  'gallery.description': 'Gallery description',
  'gallery.images': 'Photo gallery',
  'beliefs.hero.title': 'Page title',
  'beliefs.intro': 'Introduction',
  'beliefs.section_title': 'Section heading',
  'beliefs.items': 'Core beliefs',
  'beliefs.values.title': 'Our Values title',
  'beliefs.values.items': 'Our Values content',
  'visit.hero.title': 'Page title',
  'visit.welcome': 'Welcome message',
  'watch.hero.title': 'Page title',
};

const HOME_FIELD_ORDER = {
  'home.hero.slides': 100,
  'home.hero.welcome': 200,
  'home.hero.name_main': 300,
  'home.hero.name_secondary': 400,
  'home.hero.description': 500,
  'home.about.title': 1000,
  'home.about.p1': 1100,
  'home.about.p2': 1200,
  'home.about.image': 1300,
  'home.verses.items': 1400,
  'home.pastor.background': 2000,
  'home.pastor.title': 2100,
  'home.pastor.p1': 2200,
  'home.pastor.p2': 2300,
  'home.pastor.image': 2400,
  'home.pastor.name': 2500,
  'home.sermons.title': 3000,
  'home.sermons.items': 3100,
  'home.sermons.moreLink': 3200,
  'home.services.title': 4000,
  'home.services.items': 4100,
  'home.convention.title': 4500,
  'home.convention.flyer': 4600,
  'home.convention.theme_label': 4700,
  'home.convention.theme': 4800,
  'home.convention.speaker': 4900,
  'home.convention.location': 4950,
  'calendar.customEvents': 5000,
  'home.join.title': 6000,
  'home.join.description': 6100,
};

const STORY_FIELD_ORDER = {
  'story.hero.title': 100,
  'story.heading': 200,
  'story.p1': 300,
  'story.p2': 400,
  'story.p3': 500,
  'story.quote1': 600,
  'story.p4': 700,
  'story.quote2': 800,
  'story.p5': 900,
  'story.pastor_image': 1000,
  'story.image_title': 1100,
  'story.mission.title': 1200,
  'story.mission.description': 1300,
};

const MINISTRIES_FIELD_ORDER = {
  'ministries.hero.title': 100,
  'ministries.description': 200,
  'ministries.description_sub': 300,
};

const GALLERY_FIELD_ORDER = {
  'gallery.hero.title': 100,
  'gallery.description': 200,
  'gallery.images': 300,
};

const BELIEFS_FIELD_ORDER = {
  'beliefs.hero.title': 100,
  'beliefs.intro': 200,
  'beliefs.section_title': 300,
  'beliefs.items': 350,
  'beliefs.values.title': 900,
  'beliefs.values.items': 910,
};

const VISIT_FIELD_ORDER = {
  'visit.hero.title': 100,
  'visit.welcome': 200,
  'calendar.customEvents': 300,
};

const WATCH_FIELD_ORDER = {
  'watch.hero.title': 100,
};

function getSectionId(contentKey) {
  if (contentKey.startsWith('calendar.')) return 'calendar';
  if (contentKey.startsWith('home.hero.')) return 'hero';
  if (contentKey.startsWith('home.marquee.')) return 'marquee';
  if (contentKey.startsWith('home.about.')) return 'about';
  if (contentKey === 'home.verses.items') return 'verse';
  if (contentKey.startsWith('home.verse.')) return 'verse';
  if (contentKey.startsWith('home.pastor.')) return 'pastor';
  if (contentKey.startsWith('home.sermons.')) return 'sermons';
  if (contentKey.startsWith('home.services.')) return 'services';
  if (contentKey.startsWith('home.convention.')) return 'convention';
  if (contentKey.startsWith('home.join.')) return 'join';
  if (contentKey === 'story.hero.title') return 'hero';
  if (contentKey.startsWith('story.mission.')) return 'mission';
  if (contentKey === 'story.pastor_image' || contentKey === 'story.image_title') return 'photo';
  if (contentKey.startsWith('story.')) return 'story';
  if (contentKey === 'ministries.hero.title') return 'hero';
  if (contentKey === 'ministries.description' || contentKey === 'ministries.description_sub') return 'intro';
  if (contentKey.startsWith('ministries.')) return 'cards';
  if (contentKey.startsWith('gallery.')) return contentKey === 'gallery.hero.title' ? 'hero' : 'gallery';
  if (contentKey.startsWith('beliefs.')) {
    if (['beliefs.hero.title', 'beliefs.intro', 'beliefs.section_title'].includes(contentKey)) return 'hero';
    if (contentKey === 'beliefs.items' || /^beliefs\.\d+\./.test(contentKey)) return 'beliefs';
    if (contentKey === 'beliefs.values.title' || contentKey === 'beliefs.values.items' || contentKey.startsWith('beliefs.values.')) {
      return 'values';
    }
    return 'beliefs';
  }
  if (contentKey.startsWith('visit.')) return 'hero';
  if (contentKey === 'watch.hero.title') return 'hero';
  if (contentKey.startsWith('watch.section.')) return 'sections';

  const section = contentKey.split('.')[1];
  return section || 'general';
}

function getSectionTitle(sectionId) {
  return SECTION_TITLES[sectionId] || sectionId.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function getFieldOrderIndex(contentKey, pathname) {
  const fixedMaps = {
    '/': HOME_FIELD_ORDER,
    '/story': STORY_FIELD_ORDER,
    '/ministries': MINISTRIES_FIELD_ORDER,
    '/gallery': GALLERY_FIELD_ORDER,
    '/beliefs': BELIEFS_FIELD_ORDER,
    '/visit-us': VISIT_FIELD_ORDER,
    '/watch': WATCH_FIELD_ORDER,
  };

  const fixed = fixedMaps[pathname]?.[contentKey];
  if (fixed !== undefined) return fixed;

  let match = contentKey.match(/^home\.marquee\.(\d+)$/);
  if (match) return 600 + Number(match[1]);

  match = contentKey.match(/^home\.verse\.(\d+)\.(text|ref)$/);
  if (match) {
    const base = 1500 + Number(match[1]) * 20;
    return match[2] === 'text' ? base : base + 10;
  }

  match = contentKey.match(/^ministries\.(\d+)\.(cover|name|description|images)$/);
  if (match) {
    const base = 400 + Number(match[1]) * 100;
    const offsets = { cover: 0, name: 10, description: 20, images: 30 };
    return base + (offsets[match[2]] ?? 0);
  }

  match = contentKey.match(/^beliefs\.(\d+)\.(title|description|verse_text|verse_ref)$/);
  if (match) {
    const base = 400 + Number(match[1]) * 100;
    const offsets = { title: 0, description: 10, verse_text: 20, verse_ref: 30 };
    return base + (offsets[match[2]] ?? 0);
  }

  match = contentKey.match(/^watch\.section\.(\d+)\.(title|videos)$/);
  if (match) {
    const base = 200 + Number(match[1]) * 100;
    return base + (match[2] === 'title' ? 0 : 10);
  }

  const sectionOrder = PAGE_SECTION_ORDER[pathname] || [];
  const sectionId = getSectionId(contentKey);
  const sectionIndex = sectionOrder.indexOf(sectionId);
  return (sectionIndex >= 0 ? sectionIndex : 99) * 10000 + contentKey.charCodeAt(0);
}

export function getPanelFieldLabel(contentKey) {
  if (PANEL_LABELS[contentKey]) return PANEL_LABELS[contentKey];

  let match = contentKey.match(/^home\.marquee\.(\d+)$/);
  if (match) return `Banner phrase ${Number(match[1]) + 1}`;

  match = contentKey.match(/^home\.verse\.(\d+)\.(text|ref)$/);
  if (match) {
    const verseNumber = Number(match[1]) + 1;
    return match[2] === 'text' ? `Verse ${verseNumber} — text` : `Verse ${verseNumber} — reference`;
  }

  match = contentKey.match(/^ministries\.(\d+)\.(cover|name|description|images)$/);
  if (match) {
    const ministryNumber = Number(match[1]) + 1;
    const labels = {
      cover: 'Cover image',
      name: 'Ministry name',
      description: 'Full description',
      images: 'Photo gallery',
    };
    return `Ministry ${ministryNumber} — ${labels[match[2]] || match[2]}`;
  }

  match = contentKey.match(/^beliefs\.(\d+)\.(title|description|verse_text|verse_ref)$/);
  if (match) {
    const beliefNumber = Number(match[1]) + 1;
    const labels = {
      title: 'Title',
      description: 'Description',
      verse_text: 'Bible verse',
      verse_ref: 'Verse reference',
    };
    return `Belief ${beliefNumber} — ${labels[match[2]] || match[2]}`;
  }

  match = contentKey.match(/^watch\.section\.(\d+)\.(title|videos)$/);
  if (match) {
    const sectionTitle = DEFAULT_WATCH_SECTIONS[Number(match[1])]?.title || `Section ${Number(match[1]) + 1}`;
    return match[2] === 'title' ? `${sectionTitle} — title` : `${sectionTitle} — videos`;
  }

  const formatted = formatContentLabel(contentKey);
  const dashIndex = formatted.indexOf('—');
  if (dashIndex !== -1) return formatted.slice(dashIndex + 1).trim();
  return formatted;
}

export function groupFieldsByPage(fields, pathname = '/') {
  const sectionOrder = PAGE_SECTION_ORDER[pathname] || [];
  const groups = new Map();

  fields.forEach((field) => {
    const sectionId = getSectionId(field.key);
    if (!groups.has(sectionId)) {
      groups.set(sectionId, {
        sectionId,
        section: getSectionTitle(sectionId),
        items: [],
      });
    }

    groups.get(sectionId).items.push({
      ...field,
      label: getPanelFieldLabel(field.key),
      section: getSectionTitle(sectionId),
    });
  });

  groups.forEach((group) => {
    group.items.sort((a, b) => {
      const orderDiff = getFieldOrderIndex(a.key, pathname) - getFieldOrderIndex(b.key, pathname);
      if (orderDiff !== 0) return orderDiff;
      return a.key.localeCompare(b.key);
    });
  });

  const orderedGroups = [];

  sectionOrder.forEach((sectionId) => {
    const group = groups.get(sectionId);
    if (group) {
      orderedGroups.push({ section: group.section, items: group.items });
      groups.delete(sectionId);
    }
  });

  Array.from(groups.values())
    .sort((a, b) => getFieldOrderIndex(a.items[0]?.key || '', pathname) - getFieldOrderIndex(b.items[0]?.key || '', pathname))
    .forEach((group) => {
      orderedGroups.push({ section: group.section, items: group.items });
    });

  return orderedGroups;
}

export function getFieldSectionName(contentKey, pathname = '/') {
  return getSectionTitle(getSectionId(contentKey));
}
